import { cn } from '@/lib/utils';
import { type TripMode, useTripModeStore } from '@/stores/tripMode';
import { Eye, Pencil } from 'lucide-react';

/**
 * The trip page's header mode switch. View = look only (checkboxes are inert, so
 * you can't pack an item by accident). Edit = check-off enabled. Tapping Edit a
 * second time opens EVERY list for reorder + quick-add ("edit all") — the label
 * stays "Edit", the glow gets heavier. A segmented pill with the active half
 * filled so the current mode is obvious at a glance.
 */
export function TripModeToggle() {
  const mode = useTripModeStore((state) => state.mode);
  const editAll = useTripModeStore((state) => state.editAll);
  const setMode = useTripModeStore((state) => state.setMode);
  const toggleEditAll = useTripModeStore((state) => state.toggleEditAll);
  const editingAll = mode === 'edit' && editAll;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border bg-[var(--card)] p-0.5 transition-shadow',
        // Edit mode = check-off is live; make it unmistakable with a teal glow.
        // Edit-all = a different color entirely (orange) so the two states can't
        // be confused at a glance.
        editingAll
          ? 'border-[#E8792B] shadow-[0_0_0_3px_#E8792B,0_0_22px_6px_rgba(232,121,43,0.7)]'
          : mode === 'edit'
            ? 'border-[var(--primary)] shadow-[0_0_0_2px_var(--primary),0_0_16px_3px_rgba(28,124,122,0.6)]'
            : 'border-[var(--border)] shadow-sm',
      )}
    >
      <Segment
        icon={<Eye className="h-4 w-4" />}
        label="View"
        active={mode === 'view'}
        onSelect={() => setMode('view')}
      />
      <Segment
        icon={<Pencil className="h-4 w-4" />}
        label="Edit"
        active={mode === 'edit'}
        emphasis={editingAll ? 'all' : undefined}
        title={
          editingAll
            ? 'Editing all lists — tap to stop (E)'
            : mode === 'edit'
              ? 'Edit — tap again to edit all lists (E)'
              : 'Edit — enable check-off (E)'
        }
        // Second tap on the active segment toggles the edit-all pass.
        onSelect={() => (mode === 'edit' ? toggleEditAll() : setMode('edit'))}
      />
    </div>
  );
}

function Segment({
  icon,
  label,
  active,
  emphasis,
  title,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  /** 'all' = the edit-all pass; the active fill goes orange to match the glow. */
  emphasis?: 'all';
  title?: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={title}
      title={title}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? emphasis === 'all'
            ? 'bg-[#E8792B] text-white'
            : label === 'Edit'
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
              : 'bg-[#5A5A5A] text-white'
          : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/** Helper for consumers that only need the current mode boolean. */
export function useCanCheckOff(): boolean {
  return useTripModeStore((state) => state.mode === 'edit');
}

/** True while every list card should be in its "Edit list" state. */
export function useEditAllLists(): boolean {
  return useTripModeStore((state) => state.mode === 'edit' && state.editAll);
}

export type { TripMode };
